import React, { useEffect, useRef, useState } from "react";
import { Box, Divider, InputAdornment, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import { ArrowLeft, SortAsc, SortDesc } from "lucide-react";

// Components
import EpisodeItem from "./common/EpisodeItem";

// Utils
import { DebouncedCommit, createDebouncedCommit } from "./common/debouncedCommit";
import { RUNS_SEARCH_DEBOUNCE_MS, RunsFilter, RunsSlice } from "./common/runsQuery";

// Types
import { SeriesSerializer, SeriesType, ShareRunMapData } from "./types/MeGenTypes";

// Styles
import {
  DashedEmptyState,
  DividerBox,
  LoadMoreButton,
  ScrollableList,
  SearchInput,
  SortButton,
  ToolbarRow,
} from "./styles/ProjectDetail";

// ─── Styled ───────────────────────────────────────────────

const PanelRoot = styled(Box)<{ visible: boolean }>(({ visible }) => ({
  flex: visible ? 6 : 0,
  minWidth: 0,
  padding: visible ? "0 0 0 1rem" : "0",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  opacity: visible ? 1 : 0,
  transform: visible ? "translateX(0)" : "translateX(20px)",
  transition: "flex 0.3s ease, opacity 0.3s ease, transform 0.3s ease, padding 0.3s ease",
}));

const PanelHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  marginBottom: "0.75rem",
  flexShrink: 0,
});

const BackBtn = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "4px",
  borderRadius: "0.375rem",
  cursor: "pointer",
  color: "#64748b",
  "&:hover": { backgroundColor: "#f1f5f9" },
});

const SeriesName = styled(Typography)({
  fontSize: "0.9rem",
  fontWeight: 600,
  color: "#1e293b",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  flex: 1,
});

const RunCount = styled(Typography)({
  fontSize: "0.75rem",
  color: "#94a3b8",
  whiteSpace: "nowrap",
});

const NewEpisodeButton = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0.25rem",
  padding: "4px 12px",
  backgroundColor: "#fce4ec",
  color: "#e91e63",
  borderRadius: "0.5rem",
  cursor: "pointer",
  fontSize: "0.8rem",
  fontWeight: 500,
  whiteSpace: "nowrap",
  transition: "background-color 0.2s",
  flexShrink: 0,
  "&:hover": { backgroundColor: "#f8bbd9" },
});

// ─── Props ────────────────────────────────────────────────

type EpisodePanelSectionProps = {
  visible: boolean;
  selectedSeriesData?: SeriesSerializer;
  /** ผลค้น/แบ่งหน้าจาก server ของ series ที่เลือก — ไม่ต้องกรองซ้ำในนี้ */
  runs: RunsSlice;
  onSearchRuns?: (filter: RunsFilter, mode?: "replace" | "append") => void;
  onBack: () => void;
  onCreateEpisode: () => void;
  onSelectEpisode?: (episodeId: string, runType?: SeriesType) => void;
  onRestartEpisode?: (episodeId: string) => void;
  onDuplicateEpisode?: (episodeId: string) => void;
  onDeleteEpisode?: (episodeId: string) => void;
  onShareEpisode?: (shareData: ShareRunMapData) => void;
};

// ─── Component ────────────────────────────────────────────

const EpisodePanelSection: React.FC<EpisodePanelSectionProps> = (props) => {

  const { q, sort, seriesId, items, total, hasMore, loading } = props.runs;

  // ข้อความในช่องค้น เก็บแยกจาก state กลาง เพื่อให้พิมพ์ลื่นโดยไม่ต้องรอ round-trip
  const [text, setText] = useState(q);

  // ref กัน closure เก่าค้างใน debounce ที่สร้างครั้งเดียว
  // (รวม seriesId — คำค้นต้องยิงใส่ series ที่เปิดอยู่ตอน commit)
  const searchRef = useRef(props.onSearchRuns);
  const filterRef = useRef({ sort, seriesId });
  searchRef.current = props.onSearchRuns;
  filterRef.current = { sort, seriesId };

  const commitRef = useRef<DebouncedCommit<string> | null>(null);
  if (!commitRef.current) {
    commitRef.current = createDebouncedCommit<string>(
      (value) => searchRef.current?.({ q: value, ...filterRef.current }, "replace"),
      RUNS_SEARCH_DEBOUNCE_MS
    );
  }
  const debounced = commitRef.current;

  // ออกจากหน้าไปแล้วไม่ต้องยิงค้นหาที่ค้างอยู่
  useEffect(() => () => debounced.cancel(), [debounced]);

  // เปลี่ยน series → ทิ้งคำค้นที่ยังไม่ทัน commit (ไม่งั้นจะไปยิงใส่ series ใหม่)
  // ต้องประกาศ effect นี้ "ก่อน" effect sync ข้างล่าง — ลำดับการรันทำให้ค่าที่ค้างถูกทิ้งก่อน
  // แล้วช่องค้นจึงตามค่า q ที่ถูกล้างไปแล้ว
  useEffect(() => {
    debounced.cancel();
  }, [seriesId, debounced]);

  // sync คำค้นจาก state กลาง (เช่น series เปลี่ยนแล้วคำค้นถูกล้าง) — แต่ห้ามทับขณะผู้ใช้กำลังพิมพ์ค้างอยู่
  useEffect(() => {
    if (!debounced.hasPending()) setText(q);
  }, [q, seriesId, debounced]);

  const handleToggleSort = () => {
    // ส่งคำค้นที่เห็นบนจอไปพร้อมลำดับใหม่ แล้วยกเลิกตัวที่ค้าง ไม่ให้ยิงซ้ำด้วย sort เก่า
    debounced.cancel();
    searchRef.current?.({ q: text.trim(), sort: sort === "newest" ? "oldest" : "newest", seriesId }, "replace");
  };

  // ใช้ค่าที่ commit แล้ว ไม่ใช่ค่าในช่องพิมพ์ — หน้าถัดไปต้องต่อจากผลชุดที่แสดงอยู่
  const handleLoadMore = () => searchRef.current?.({ q, sort, seriesId }, "append");

  return (
    <>
      {/* Divider */}
      <Divider
        orientation="vertical"
        flexItem
        sx={{
          margin: "0.5rem 0",
          opacity: props.visible ? 1 : 0,
          transition: "opacity 0.3s ease",
        }}
      />

      <PanelRoot id="megen-episode-panel-root" visible={props.visible}>
        {/* Header */}
        <PanelHeader id="megen-episode-panel-header">
          <BackBtn id="megen-episode-panel-back-btn" onClick={props.onBack}>
            <ArrowLeft size={18} />
          </BackBtn>

          <SeriesName id="megen-episode-panel-series-name" title={props.selectedSeriesData?.name}>
            {`Series: ${props.selectedSeriesData?.name || ""}`}
          </SeriesName>

          <RunCount id="megen-episode-panel-run-count">
            {/* จำนวนทั้งหมดจาก server ไม่ใช่ที่โหลดมาแล้ว (แบ่งหน้า) */}
            {loading && items.length === 0 ? "…" : `${total} run${total !== 1 ? "s" : ""}`}
          </RunCount>

          <NewEpisodeButton id="megen-episode-panel-new-episode-btn" onClick={props.onCreateEpisode}>
            <AddIcon sx={{ fontSize: "1rem" }} />
            New Video
          </NewEpisodeButton>
        </PanelHeader>

        <DividerBox />

        {/* Toolbar */}
        <ToolbarRow id="megen-episode-panel-toolbar" sx={{ marginTop: 0, marginBottom: "0.75rem" }}>
          <SearchInput
            id="megen-episode-panel-search-input"
            placeholder="Search runs..."
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              debounced.push(e.target.value);
            }}
            onBlur={() => debounced.flush()}
            onKeyDown={(e) => {
              if (e.key === "Enter") debounced.flush();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8", fontSize: "1.25rem" }} />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={sort === "newest" ? "Newest First" : "Oldest First"}>
            <SortButton id="megen-episode-panel-sort-btn" onClick={handleToggleSort}>
              {sort === "newest" ? <SortDesc size={18} /> : <SortAsc size={18} />}
            </SortButton>
          </Tooltip>
        </ToolbarRow>

        {/* Episode List */}
        <ScrollableList id="megen-episode-panel-list">
          {items.length === 0 ? (
            <DashedEmptyState id="megen-episode-panel-empty-state">
              <Typography sx={{ color: "#94a3b8", fontSize: "0.9rem" }}>
                {loading ? (
                  "กำลังค้นหา…"
                ) : q ? (
                  <>No runs found matching "{q}".</>
                ) : (
                  <>ยังไม่มี Episode ใน Series นี้ คลิก <strong>"New Video"</strong> เพื่อเริ่มต้น</>
                )}
              </Typography>
            </DashedEmptyState>
          ) : (
            <>
              {items.map((run) => (
                <EpisodeItem
                  key={run.id}
                  episode={run}
                  onSelect={props.onSelectEpisode}
                  onRestart={props.onRestartEpisode}
                  onDuplicate={props.onDuplicateEpisode}
                  onDelete={props.onDeleteEpisode}
                  onShare={props.onShareEpisode}
                />
              ))}

              {hasMore && (
                <LoadMoreButton id="megen-episode-panel-load-more-btn" onClick={handleLoadMore} disabled={loading}>
                  {loading ? "กำลังโหลด…" : `โหลดเพิ่ม (แสดง ${items.length} จาก ${total})`}
                </LoadMoreButton>
              )}
            </>
          )}
        </ScrollableList>
      </PanelRoot>
    </>
  );
};

export default EpisodePanelSection;
